import React, {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../store.ts";
import {getAgencyAssetUrl} from "../utils/AssetsCDN.ts";
import {Button} from "./Button.tsx";
import {Input} from "./Input.tsx";
import {Select} from "./Select.tsx";
import {AutocompleteSearch} from "./AutocompleteSearch.tsx";
import {ConfirmDialog} from "./dialogs/ConfirmDialog.tsx";
import {DeleteAgencyUseCase} from "../domain/useCases/agencies/DeleteAgencyUseCase.ts";
import {UpdateAgencyUseCase} from "../domain/useCases/agencies/UpdateAgencyUseCase.ts";
import {GetAgencyReferralAgreementUseCase} from "../domain/useCases/agencies/GetAgencyReferralAgreementUseCase.ts";
import {GetAgencyReferralsUseCase} from "../domain/useCases/agencies/GetAgencyReferralsUseCase.ts";
import {GetAgencyPackagesUseCase} from "../domain/useCases/agencies/GetAgencyPackagesUseCase.ts";
import {AddAgencyPackageUseCase} from "../domain/useCases/agencies/AddAgencyPackageUseCase.ts";
import {UpdateAgencyPackageUseCase} from "../domain/useCases/agencies/UpdateAgencyPackageUseCase.ts";
import {DeleteAgencyPackageUseCase} from "../domain/useCases/agencies/DeleteAgencyPackageUseCase.ts";
import {AgencyRepository} from "../repositories/AgencyRepository.ts";
import {AuthenticationRepository} from "../repositories/AuthenticationRepository.ts";
import {GetAgencyByIdUseCase} from "../domain/useCases/agencies/GetAgencyByIdUseCase.ts";
import {setAgenciesStore} from "../features/agencies.ts";
import {useAgencies} from "../hooks/useAgencies.ts";
import {ReferralAgreement, TripbookPackage} from "../domain/entities/Agency.ts";
import {initialsOf} from "../utils/initials.ts";
import IconCamera from '../assets/icons/cameraIcon.svg?react';
import IconBackArrow from '../assets/icons/backArrowIcon.svg?react';
import IconSpinner from '../assets/icons/spinner.svg?react';
import IconCopy from '../assets/icons/copy.svg?react';
import IconChevronDown from '../assets/icons/chevron-down.svg?react';
import {getMarketOptions, normalizeMarketValue, resolveAgencyMarket} from "./agenciesTable/marketOptions.ts";
import {AgencyStatsSection} from "./agencyDetail/AgencyStatsSection.tsx";
import {PacksTripbooksSection} from "./agencyDetail/PacksTripbooksSection.tsx";
import {NewPaymentPlanForm, PaymentPlanDetails, PaymentPlanToggle} from "./agencyDetail/PaymentPlans.tsx";
import {usePaymentPlans} from "../hooks/usePaymentPlans.ts";
import {PaymentPlan} from "../domain/entities/PaymentPlan.ts";
import {AgencyUsersSection} from "./agencyDetail/AgencyUsersSection.tsx";
import {AgencyObservationsSection} from "./agencyDetail/AgencyObservationsSection.tsx";
import {useB2BAgencyStats} from "../hooks/useB2BAgencyStats.ts";

type AgencyDetailTab = 'stats' | 'packs' | 'config' | 'observations'

// Hysteresis for the collapsible header: collapse past 48px of content scroll,
// expand again only near the top, so it doesn't flicker around one threshold.
const HEADER_COLLAPSE_AT = 48
const HEADER_EXPAND_AT = 8
// h-11 compact bar (44px) + its mb-1.
const COMPACT_HEADER_HEIGHT = 48

interface AgencyDetailProps {
    agencyId: string
    setPath: (path: string) => void
}

const agencyRepo = new AgencyRepository(new AuthenticationRepository())
const getAgencyByIdUseCase = new GetAgencyByIdUseCase(agencyRepo);
const deleteAgencyUseCase = new DeleteAgencyUseCase(agencyRepo);
const updateAgencyUseCase = new UpdateAgencyUseCase(agencyRepo);
const getReferralAgreementUseCase = new GetAgencyReferralAgreementUseCase(agencyRepo);
const getReferralsUseCase = new GetAgencyReferralsUseCase(agencyRepo);
const getPackagesUseCase = new GetAgencyPackagesUseCase(agencyRepo);
const addPackageUseCase = new AddAgencyPackageUseCase(agencyRepo);
const updatePackageUseCase = new UpdateAgencyPackageUseCase(agencyRepo);
const deletePackageUseCase = new DeleteAgencyPackageUseCase(agencyRepo);

export const AgencyDetailContainer: React.FC<AgencyDetailProps> = ({agencyId, setPath}) => {
    const {t} = useTranslation()
    const dispatch = useDispatch()
    const agenciesStore = useSelector((state: RootState) => state.agencies.agencies)
    const {agencies: allAgencies} = useAgencies()
    const user = useSelector((state: RootState) => state.auth.user)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isAdmin = (user as any)?.role === 'admin'
    const confirmDeleteDialog = useRef<React.ElementRef<typeof ConfirmDialog<boolean>>>(null)
    const fileInput = useRef<HTMLInputElement>(null)

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [agency, setAgency] = useState<any>(null)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [edited, setEdited] = useState<any>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [copiedId, setCopiedId] = useState(false)
    const [activeTab, setActiveTab] = useState<AgencyDetailTab>('stats')
    const {stats, isLoadingStats} = useB2BAgencyStats(agencyId)

    // Scrolling the tab content swaps the header + KPI strip for a one-line bar,
    // so the code tables get the vertical room. Instant swap, no transition —
    // the project's UI rules forbid scroll-driven animations.
    const contentRef = useRef<HTMLDivElement>(null)
    const expandedHeaderRef = useRef<HTMLDivElement>(null)
    const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false)

    const handleContentScroll = () => {
        const el = contentRef.current
        if (!el) return
        if (!isHeaderCollapsed && el.scrollTop > HEADER_COLLAPSE_AT) {
            // Only collapse when the content would still overflow afterwards:
            // otherwise the freed height drops scrollTop back to 0, the header
            // expands again and the two states flip-flop.
            const freedHeight = (expandedHeaderRef.current?.offsetHeight ?? 0) - COMPACT_HEADER_HEIGHT
            if (el.scrollHeight - el.clientHeight > freedHeight + HEADER_COLLAPSE_AT) setIsHeaderCollapsed(true)
        } else if (isHeaderCollapsed && el.scrollTop < HEADER_EXPAND_AT) {
            setIsHeaderCollapsed(false)
        }
    }

    const expandHeader = () => {
        contentRef.current?.scrollTo({top: 0})
        setIsHeaderCollapsed(false)
    }

    // The content container is shared by every tab, so a new tab would
    // otherwise open at the previous tab's scroll offset.
    const changeTab = (tab: AgencyDetailTab) => {
        setActiveTab(tab)
        expandHeader()
    }

    // Referral
    const [referralSearch, setReferralSearch] = useState('')
    const [referrals, setReferrals] = useState<ReferralAgreement[]>([])
    const [referralsLoading, setReferralsLoading] = useState(false)

    // Packages
    const [packages, setPackages] = useState<TripbookPackage[]>([])
    const [packagesLoading, setPackagesLoading] = useState(false)
    const [showAddPackage, setShowAddPackage] = useState(false)
    const [pkgUnitPrice, setPkgUnitPrice] = useState('')
    const [pkgQuantity, setPkgQuantity] = useState('')
    const [pkgDate, setPkgDate] = useState(() => new Date().toISOString().split('T')[0])
    // Blank = the pack follows the agency's own numberOfPagesIncluded.
    const [pkgPages, setPkgPages] = useState('')
    const [pkgSaving, setPkgSaving] = useState(false)

    // Edit package
    const [editingPkgId, setEditingPkgId] = useState<string | null>(null)
    const [editPkgUnitPrice, setEditPkgUnitPrice] = useState('')
    const [editPkgQuantity, setEditPkgQuantity] = useState('')
    const [editPkgDate, setEditPkgDate] = useState('')
    const [editPkgPages, setEditPkgPages] = useState('')
    const [editPkgSaving, setEditPkgSaving] = useState(false)
    const [deletingPkgId, setDeletingPkgId] = useState<string | null>(null)

    // Packs charged through Stripe (admin-only). A plan with no payment yet is a
    // pending row in the packages table; once paid it hangs off its package.
    const paymentPlans = usePaymentPlans(agencyId, isAdmin)
    const [addPackageMode, setAddPackageMode] = useState<'manual' | 'stripe'>('manual')
    const [expandedPlanId, setExpandedPlanId] = useState<string | null>(null)

    useEffect(() => {
        if (!agencyId) return
        setIsLoading(true)
        getAgencyByIdUseCase.execute(agencyId).then((res: any) => {
            const enriched = {
                ...res,
                primaryMarket: normalizeMarketValue(res.primaryMarket),
                invitationLandingTemplate: extractTemplateFromUrl(res.design?.invitationPage?.headerBackgroundImage),
                _captureItinerary: res.invitationEngagementFlow
                    ? res.invitationEngagementFlow.includes('itinerary')
                    : true,
                _captureDIY: res.invitationEngagementFlow
                    ? res.invitationEngagementFlow.includes('experienceSelection')
                    : true,
            }
            setAgency(enriched)
            setEdited(enriched)
            return enriched.referredByAgencyId ? getReferralAgreementUseCase.execute(agencyId) : undefined
        }).then((agreement) => {
            if (!agreement) return
            const referralPatch = {
                referralCommissionPct: agreement.commissionPct,
                referralValidFrom: agreement.validFrom,
                referralValidUntil: agreement.validUntil,
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            setAgency((prev: any) => prev ? {...prev, ...referralPatch} : prev)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            setEdited((prev: any) => prev ? {...prev, ...referralPatch} : prev)
        }).finally(() => setIsLoading(false))
    }, [agencyId])

    useEffect(() => {
        if (!agencyId) return
        setPackagesLoading(true)
        getPackagesUseCase.execute(agencyId)
            .then(res => res.length > 0 ? setPackages(res) : setPackages([]))
            .finally(() => setPackagesLoading(false))
    }, [agencyId])

    useEffect(() => {
        if (!agencyId) return
        setReferralsLoading(true)
        getReferralsUseCase.execute(agencyId)
            .then(res => setReferrals(res ?? []))
            .catch(() => setReferrals([]))
            .finally(() => setReferralsLoading(false))
    }, [agencyId])

    const hasChanges = agency && edited && JSON.stringify(agency) !== JSON.stringify(edited)

    const referralAgencyOptions = useMemo(() => {
        if (!allAgencies || !referralSearch) return []
        return allAgencies
            .filter((a) => a.id !== agencyId && a.nameLowerCase?.includes(referralSearch.toLowerCase()))
            .map((a) => ({value: a.id, label: a.name, data: a}))
            .slice(0, 10)
    }, [allAgencies, referralSearch, agencyId])

    const referralAgencyName = useMemo(() => {
        if (!edited?.referredByAgencyId) return ''
        return allAgencies?.find((a) => a.id === edited.referredByAgencyId)?.name ?? edited.referredByAgencyId
    }, [allAgencies, edited?.referredByAgencyId])

    const agencyNameById = useMemo(() => {
        const map = new Map<string, string>()
        allAgencies?.forEach((a) => map.set(a.id, a.name))
        return map
    }, [allAgencies])

    const buildEngagementFlow = (captureItinerary: boolean, captureDIY: boolean): string => {
        const base = ['itinerary', 'experienceSelection', 'login', 'contactPhone']
        return base.filter(step => {
            if (step === 'itinerary') return captureItinerary
            if (step === 'experienceSelection') return captureDIY
            return true
        }).join(',')
    }

    const handleSave = () => {
        if (!edited) return
        setIsSaving(true)
        updateAgencyUseCase.execute(agencyId, {
            name: edited.name,
            contactFirstName: edited.contactFirstName ?? '',
            contactLastName: edited.contactLastName ?? '',
            contactPhone: edited.contactPhone,
            contactEmail: edited.contactEmail,
            website: edited.website,
            market: edited.primaryMarket,
            logo: edited._newLogoFile,
            primaryColor: edited.color,
            coverMainColorHex: edited.coverMainColorHex ?? '#FFFFFF',
            genericCode: edited.genericCode,
            codePrefix: edited.codePrefix,
            referredByAgencyId: edited.referredByAgencyId,
            referralCommissionPct: edited.referredByAgencyId ? edited.referralCommissionPct : undefined,
            referralValidFrom: edited.referredByAgencyId ? edited.referralValidFrom : undefined,
            referralValidUntil: edited.referredByAgencyId ? edited.referralValidUntil : undefined,
            invitationLandingTemplate: edited.invitationLandingTemplate,
            numberOfPagesIncluded: edited.numberOfPagesIncluded,
            clientPlannerUrl: edited.clientPlannerUrl,
            invertedLogo: edited.invertedLogo,
            webLinkQRCredits: edited.webLinkQRCredits,
            dedicationText: edited.dedicationText,
            fiscalName: edited.fiscalName,
            fiscalAddress: edited.fiscalAddress,
            fiscalId: edited.fiscalId,
            showLogoInIntro: edited.showLogoInIntro,
            introductionText: edited.introductionText,
            reviewer: edited.reviewer,
            invitationEngagementFlow: buildEngagementFlow(edited._captureItinerary ?? true, edited._captureDIY ?? true),
            tripbookAgencyBindingName: edited.tripbookAgencyBindingName,
        }).then(() => {
            const saved = {...edited, _newLogoFile: undefined}
            setAgency(saved)
            if (agenciesStore) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const updated = agenciesStore.map((a: any) => a.id === agencyId ? saved : a)
                dispatch(setAgenciesStore(updated))
            }
        }).finally(() => setIsSaving(false))
    }

    const handleDelete = () => {
        deleteAgencyUseCase.execute(agencyId).then(() => {
            if (agenciesStore) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                dispatch(setAgenciesStore(agenciesStore.filter((a: any) => a.id !== agencyId)))
            }
            setPath('agencies')
        })
    }

    const handleLogoFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) setEdited({...edited, _newLogoFile: file})
        e.target.value = ''
    }

    const startEditPackage = (pkg: TripbookPackage) => {
        setEditingPkgId(pkg.id)
        setEditPkgUnitPrice(String(pkg.unitPrice))
        setEditPkgQuantity(String(pkg.quantity))
        setEditPkgDate(new Date(pkg.date).toISOString().split('T')[0])
        setEditPkgPages(pkg.numberOfPagesIncluded != null ? String(pkg.numberOfPagesIncluded) : '')
    }

    // A pack's page count, or the agency's when the pack doesn't set one.
    const agencyPages: number = agency?.numberOfPagesIncluded ?? 60
    const parsePages = (value: string) => {
        const pages = parseInt(value, 10)
        return pages > 0 ? pages : undefined
    }

    const handleUpdatePackage = () => {
        if (!editingPkgId) return
        const unitPrice = parseFloat(editPkgUnitPrice)
        const quantity = parseInt(editPkgQuantity, 10)
        if (!unitPrice || !quantity || !editPkgDate) return
        const date = new Date(editPkgDate).getTime()
        const pkgId = editingPkgId
        const numberOfPagesIncluded = parsePages(editPkgPages)
        setEditPkgSaving(true)
        updatePackageUseCase.execute(agencyId, pkgId, unitPrice, quantity, date, numberOfPagesIncluded)
            .then((updated) => {
                const patch: TripbookPackage = updated ?? {
                    id: pkgId,
                    unitPrice,
                    quantity,
                    date,
                    numberOfPagesIncluded,
                    totalAmount: unitPrice * quantity,
                }
                setPackages((prev) => prev.map(p => p.id === pkgId ? patch : p))
                setEditingPkgId(null)
            })
            .catch((err) => {
                console.error('Error updating package:', err)
            })
            .finally(() => setEditPkgSaving(false))
    }

    const handleDeletePackage = (packageId: string) => {
        setDeletingPkgId(packageId)
        deletePackageUseCase.execute(agencyId, packageId)
            .then(() => {
                setPackages((prev) => prev.filter(p => p.id !== packageId))
            })
            .finally(() => setDeletingPkgId(null))
    }

    const handleAddPackage = () => {
        const unitPrice = parseFloat(pkgUnitPrice)
        const quantity = parseInt(pkgQuantity, 10)
        if (!unitPrice || !quantity || !pkgDate) return
        const date = new Date(pkgDate).getTime()
        setPkgSaving(true)
        addPackageUseCase.execute(agencyId, unitPrice, quantity, date, parsePages(pkgPages))
            .then((newPkg) => {
                setPackages((prev) => [newPkg, ...prev])
                setPkgUnitPrice('')
                setPkgQuantity('')
                setPkgPages('')
                setPkgDate(new Date().toISOString().split('T')[0])
                setShowAddPackage(false)
            })
            .finally(() => setPkgSaving(false))
    }

    if (isLoading || !agency || !edited) {
        return (
            <div className="flex items-center justify-center h-full">
                <IconSpinner className="size-10"/>
            </div>
        )
    }

    const logoPreviewUrl = edited._newLogoFile
        ? URL.createObjectURL(edited._newLogoFile)
        : agency.logoName ? getAgencyAssetUrl(agency.logoName) : null

    const currency = marketToCurrency(agency.primaryMarket)
    const totalPackages = packages?.reduce((acc, p) => acc + p.totalAmount, 0) ?? 0
    const totalUnits = packages?.reduce((acc, p) => acc + p.quantity, 0) ?? 0
    const avgTicket = packages.length ? totalPackages / packages.length : 0
    const avgUnitPrice = totalUnits ? totalPackages / totalUnits : 0
    const lastPurchase = packages.length ? packages.reduce((a, b) => (a.date > b.date ? a : b)) : null

    // Average time between consecutive pack purchases: sort by date, diff each
    // purchase against the previous one, average the gaps. Needs at least 2
    // packages to have a single gap to measure.
    const packageDates = packages.map((p) => p.date).sort((a, b) => a - b)
    const repurchaseGapsMs = packageDates.slice(1).map((date, i) => date - packageDates[i])
    const avgRepurchaseDays = repurchaseGapsMs.length
        ? repurchaseGapsMs.reduce((a, b) => a + b, 0) / repurchaseGapsMs.length / (1000 * 60 * 60 * 24)
        : null

    const pendingPlans = paymentPlans.plans.filter((plan) => !plan.packageId)
    const planByPackageId = new Map(paymentPlans.plans.filter((plan) => plan.packageId).map((plan) => [plan.packageId!, plan]))
    const togglePlan = (planId: string) => setExpandedPlanId((prev) => prev === planId ? null : planId)
    const planDetailsRow = (plan: PaymentPlan) => (
        <tr className="border-b border-neutralBlack-100">
            <td colSpan={6} className="py-2">
                <PaymentPlanDetails
                    plan={plan}
                    formatMoney={(amount) => formatCurrency(amount, currency)}
                    onRenegotiate={(installments) => paymentPlans.renegotiate(plan.id, installments)}
                    onCancel={() => paymentPlans.cancel(plan.id)}
                />
            </td>
        </tr>
    )

    const marketInfo = resolveAgencyMarket(agency)
    const isActive = agency.status ? agency.status === 'active' : true

    const copyAgencyId = () => {
        navigator.clipboard.writeText(agency.id).then(() => {
            setCopiedId(true)
            setTimeout(() => setCopiedId(false), 1500)
        })
    }

    const logoContent = (padding: string) => logoPreviewUrl
        ? <img src={logoPreviewUrl} alt={agency.name} className={`w-full h-full object-contain ${padding}`}/>
        : initialsOf(agency.name)

    const idChip = (
        <button
            type="button"
            title={t('agencyDetail.copyAgencyId', {ns: 'dashboard'})}
            onClick={copyAgencyId}
            className="flex-none inline-flex items-center gap-1.5 text-xs text-neutralBlack-600 border border-neutralBlack-200 rounded-lg px-2 py-1 bg-white hover:bg-neutralBlack-100"
        >
            {copiedId ? t('referrals.copied', {ns: 'dashboard'}) : agency.id}
            <IconCopy className="w-2.5 h-2.5"/>
        </button>
    )

    // Kept in both header variants: the Config tab is long, and unsaved
    // changes must stay saveable after scrolling down into it.
    const saveButton = hasChanges && (
        <Button
            variant="primary"
            className="max-h-[40px] min-h-[40px]"
            disabled={isSaving}
            onClick={handleSave}
        >
            {t('saveChanges', {ns: 'dashboard'})}
        </Button>
    )

    return (
        <div className="flex flex-col h-full overflow-hidden">
            <input type="file" ref={fileInput} className="hidden" onChange={handleLogoFile} accept="image/png"/>

            {isHeaderCollapsed ? (
            /* Compact header: name, flag, ID and save only — the back link,
               account manager and KPI strip come back at the top of the scroll. */
            <div className="flex items-center gap-3 h-11 mb-1">
                <span className="w-7 h-7 flex-none rounded-lg bg-neutralBlack-100 border border-neutralBlack-200 flex items-center justify-center overflow-hidden text-[11px] font-semibold text-neutralBlack-800">
                    {logoContent('p-1')}
                </span>
                <span className="min-w-0 truncate text-base font-semibold tracking-tight text-neutralBlack-900 capitalize">{agency.name}</span>
                {marketInfo?.flag && <img src={marketInfo.flag} alt={marketInfo.label} className="flex-none w-[18px] h-[13px] rounded-sm object-cover shadow-[0_0_0_1px_rgba(0,0,0,.08)]"/>}
                {idChip}
                <span className="flex-1 min-w-[12px]"/>
                {saveButton}
                <button
                    type="button"
                    title={t('agencyDetail.expandHeader', {ns: 'dashboard'})}
                    aria-label={t('agencyDetail.expandHeader', {ns: 'dashboard'})}
                    onClick={expandHeader}
                    className="flex-none w-[26px] h-[26px] flex items-center justify-center rounded-full bg-neutralBlack-100 text-neutralBlack-700 hover:bg-neutralBlack-200"
                >
                    <IconChevronDown className="w-[11px] h-[11px]"/>
                </button>
            </div>
            ) : (
            <div ref={expandedHeaderRef} className="flex flex-col pb-5">

            {/* Back link */}
            <button
                type="button"
                onClick={() => setPath('agencies')}
                className="inline-flex items-center gap-1.5 self-start text-[13px] text-neutralBlack-500 hover:text-neutralBlack-900 mb-3"
            >
                <IconBackArrow className="w-[7px] h-[11px]"/>
                {t('agencies', {ns: 'dashboard'})}
            </button>

            {/* Header */}
            <div className="flex items-start gap-5 flex-wrap">
                <span className="w-14 h-14 flex-none rounded-2xl bg-neutralBlack-100 border border-neutralBlack-200 flex items-center justify-center overflow-hidden text-base font-semibold text-neutralBlack-800">
                    {logoContent('p-1.5')}
                </span>
                <div className="min-w-[220px] flex flex-col gap-2">
                    <div className="flex items-center gap-3 flex-wrap">
                        <h1 className="text-[26px] font-semibold tracking-tight text-neutralBlack-900 capitalize">{agency.name}</h1>
                        {marketInfo && (
                            <span className="inline-flex items-center gap-1.5 text-[13px] text-neutralBlack-800">
                                {marketInfo.flag && <img src={marketInfo.flag} alt={marketInfo.label} className="w-[18px] h-[13px] rounded-sm object-cover shadow-[0_0_0_1px_rgba(0,0,0,.08)]"/>}
                                {marketInfo.label}
                            </span>
                        )}
                        {idChip}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">

                        <span className="inline-flex items-center gap-1.5 bg-white border border-neutralBlack-200 rounded-full pl-1 pr-2.5 py-0.5 text-xs text-neutralBlack-800">
                            <span className="w-5 h-5 rounded-full bg-neutralBlack-100 flex items-center justify-center text-[9.5px] font-semibold text-neutralBlack-700">
                                {initialsOf(agency.accountManagerName)}
                            </span>
                            {agency.accountManagerName ?? 'Sin asignar'}
                        </span>
                    </div>
                </div>
                <span className="flex-1 min-w-[12px]"/>
                <div className="flex items-center gap-2 flex-wrap">
                    {saveButton}
                </div>
            </div>

            {/* KPI strip */}
            <div className="flex gap-2.5 flex-wrap mt-5">
                <MiniStat label={t('agencyDetail.ltv', {ns: 'dashboard'})} value={formatCurrency(totalPackages, currency)}
                          note={packages.length ? undefined : t('agencyDetail.noPurchasesYet', {ns: 'dashboard'})}/>
                <MiniStat label={t('agencyDetail.avgTicket', {ns: 'dashboard'})} value={packages.length ? formatCurrency(avgTicket, currency) : '—'}
                          note={t('agencyDetail.perPackPurchased', {ns: 'dashboard'})}/>
                <MiniStat label={t('agencyDetail.avgRepurchaseTime', {ns: 'dashboard'})}
                          value={avgRepurchaseDays != null ? t('agencyDetail.avgRepurchaseTimeValue', {ns: 'dashboard', n: Math.round(avgRepurchaseDays)}) : '—'}
                          note={avgRepurchaseDays != null ? t('agencyDetail.avgRepurchaseTimeNote', {ns: 'dashboard', n: packages.length}) : undefined}/>
                <MiniStat label={t('agencyDetail.avgUnitPrice', {ns: 'dashboard'})} value={totalUnits ? formatCurrency(avgUnitPrice, currency) : '—'}
                          note={totalUnits ? t('agencyDetail.unitsPurchasedNote', {ns: 'dashboard', n: totalUnits}) : undefined}/>
                <MiniStat label={t('agencyDetail.lastPurchase', {ns: 'dashboard'})} value={lastPurchase ? formatDate(lastPurchase.date) : '—'}
                          note={lastPurchase ? t('agencyDetail.lastPurchaseNote', {ns: 'dashboard', qty: lastPurchase.quantity, amount: formatCurrency(lastPurchase.totalAmount, currency)}) : undefined}/>
            </div>

            </div>
            )}

            {/* Tabs */}
            <div className="flex items-center gap-6 border-b border-neutralBlack-200">
                <TabButton label={t('sidebar.stats', {ns: 'dashboard'})} active={activeTab === 'stats'} onClick={() => changeTab('stats')}/>
                <TabButton label={t('sidebar.packsAndTripbooks', {ns: 'dashboard'})} active={activeTab === 'packs'} onClick={() => changeTab('packs')}/>
                <TabButton label={t('sidebar.config', {ns: 'dashboard'})} active={activeTab === 'config'} onClick={() => changeTab('config')}/>
                {isAdmin && (
                    <TabButton label={t('sidebar.observations', {ns: 'dashboard'})} active={activeTab === 'observations'} onClick={() => changeTab('observations')}/>
                )}
            </div>

            {/* Content */}
            <div ref={contentRef} onScroll={handleContentScroll} className="overflow-y-auto no-scrollbar flex-1 pt-5">
                {activeTab === 'stats' && (
                    <AgencyStatsSection agencyId={agencyId} stats={stats} isLoading={isLoadingStats} onViewAllTripbooks={() => changeTab('packs')}/>
                )}
                {activeTab === 'packs' && (
                    <PacksTripbooksSection agencyId={agencyId} genericCode={agency.genericCode} primaryMarket={agency.primaryMarket} numberOfPagesIncluded={agency.numberOfPagesIncluded} viewAsAgency/>
                )}
                {activeTab === 'observations' && isAdmin && (
                    <div className="pb-10">
                        <AgencyObservationsSection agencyId={agencyId}/>
                    </div>
                )}
                {activeTab === 'config' && (
                <div className="flex flex-col gap-3 pb-10">

                    {/* Identidad, fiscal y contacto */}
                    <Section title="Identidad, fiscal y contacto">
                        <div className="flex items-center gap-4 flex-wrap pb-1">
                            <div
                                className="w-[120px] h-[80px] flex-none rounded-2xl bg-neutralBlack-100 border border-neutralBlack-200 flex items-center justify-center overflow-hidden cursor-pointer relative group"
                                onClick={() => fileInput.current?.click()}
                            >
                                {logoPreviewUrl
                                    ? <img className="w-full h-full object-contain p-2" alt={agency.name} src={logoPreviewUrl}/>
                                    : <IconCamera className="w-7 h-7 opacity-30"/>
                                }
                                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <IconCamera className="w-6 h-6 text-white"/>
                                </div>
                            </div>
                            <span className="flex-1 min-w-[10px]"/>
                            {isAdmin && (
                                <div className="flex flex-col gap-1.5 w-[190px]">
                                    <label className="text-xs text-neutralBlack-500">{t('primaryMarket', {ns: 'dashboard'})}</label>
                                    <Select
                                        id="primaryMarket"
                                        block
                                        value={edited.primaryMarket ?? ''}
                                        onChange={(e) => setEdited({...edited, primaryMarket: e.target.value || undefined})}
                                        placeholder={t('selectPrimaryMarket', {ns: 'dashboard'})}
                                        options={getMarketOptions()}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="h-px bg-neutralBlack-200"/>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                            <Field label={t('writeAgencyName', {ns: 'dashboard'})} required>
                                <Input id="name" type="text" invalid={false}
                                       value={edited.name ?? ''}
                                       onChange={(e) => setEdited({...edited, name: e.target.value})}
                                       className="h-10"/>
                            </Field>
                            <Field label={t('website', {ns: 'dashboard'})}>
                                <Input id="website" type="text" invalid={false}
                                       value={edited.website ?? ''}
                                       onChange={(e) => setEdited({...edited, website: e.target.value})}
                                       className="h-10"/>
                            </Field>
                            <Field label={t('codePrefix', {ns: 'dashboard'})}>
                                <Input id="codePrefix" type="text" invalid={false}
                                       value={edited.codePrefix ?? ''}
                                       onChange={(e) => setEdited({...edited, codePrefix: e.target.value || undefined})}
                                       className="h-10"/>
                            </Field>
                            {edited.genericCode && (
                                <Field label={t('genericCode', {ns: 'dashboard'})}>
                                    <Input id="genericCode" type="text" invalid={false}
                                           value={edited.genericCode ?? ''}
                                           onChange={(e) => setEdited({...edited, genericCode: e.target.value})}
                                           className="h-10"/>
                                </Field>
                            )}
                            {isAdmin && (
                                <>
                                    <Field label={t('fiscalName', {ns: 'dashboard'})}>
                                        <Input id="fiscalName" type="text" invalid={false}
                                               value={edited.fiscalName ?? ''}
                                               onChange={(e) => setEdited({...edited, fiscalName: e.target.value || undefined})}
                                               className="h-10"/>
                                    </Field>
                                    <Field label={t('fiscalId', {ns: 'dashboard'})}>
                                        <Input id="fiscalId" type="text" invalid={false}
                                               value={edited.fiscalId ?? ''}
                                               onChange={(e) => setEdited({...edited, fiscalId: e.target.value || undefined})}
                                               className="h-10"/>
                                    </Field>
                                    <Field label={t('fiscalAddress', {ns: 'dashboard'})}>
                                        <Input id="fiscalAddress" type="text" invalid={false}
                                               value={edited.fiscalAddress ?? ''}
                                               onChange={(e) => setEdited({...edited, fiscalAddress: e.target.value || undefined})}
                                               className="h-10"/>
                                    </Field>
                                </>
                            )}
                        </div>

                        <div className="h-px bg-neutralBlack-200"/>

                        <span className="text-xs text-neutralBlack-500">{t('contactPerson', {ns: 'dashboard'})}</span>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                            <Field label={t('firstName', {ns: 'dashboard'})} required>
                                <Input id="firstName" type="text" invalid={false}
                                       value={edited.contactFirstName ?? ''}
                                       onChange={(e) => setEdited({...edited, contactFirstName: e.target.value})}
                                       className="h-10"/>
                            </Field>
                            <Field label={t('lastName', {ns: 'dashboard'})} required>
                                <Input id="lastName" type="text" invalid={false}
                                       value={edited.contactLastName ?? ''}
                                       onChange={(e) => setEdited({...edited, contactLastName: e.target.value})}
                                       className="h-10"/>
                            </Field>
                            <Field label={t('phone', {ns: 'dashboard'})}>
                                <Input id="phone" type="tel" invalid={false}
                                       value={edited.contactPhone ?? ''}
                                       onChange={(e) => setEdited({...edited, contactPhone: e.target.value})}
                                       className="h-10"/>
                            </Field>
                            <Field label={t('email', {ns: 'dashboard'})}>
                                <Input id="contactEmail" type="email" invalid={false}
                                       value={edited.contactEmail ?? ''}
                                       onChange={(e) => setEdited({...edited, contactEmail: e.target.value})}
                                       className="h-10"/>
                            </Field>
                        </div>
                    </Section>

                    {/* Marca */}
                    <Section title={t('agencyDetail.brandSection', {ns: 'dashboard'})}>
                        <div className="flex gap-6 flex-wrap">
                            <div className="flex-1 min-w-[260px] flex flex-col gap-4">
                                <Field label={t('agencyCorporativeColor', {ns: 'dashboard'})}>
                                    <div className="flex items-center gap-2.5">
                                        <span className="w-[34px] h-[34px] flex-none rounded-full border border-neutralBlack-200"
                                              style={{background: edited.color ? (edited.color.startsWith('#') ? edited.color : '#' + edited.color) : '#FFFFFF'}}/>
                                        <Input id="colorHex" type="text" invalid={false} placeholder="#F8F8F8"
                                               value={edited.color ?? ''}
                                               onChange={(e) => setEdited({...edited, color: e.target.value})}
                                               className="h-10 flex-1"/>
                                    </div>
                                </Field>
                                <Field label={t('coverMainColor', {ns: 'dashboard'})}>
                                    <div className="flex items-center gap-2.5">
                                        <span className="w-[34px] h-[34px] flex-none rounded-full border border-neutralBlack-200"
                                              style={{background: edited.coverMainColorHex ?? '#FFFFFF'}}/>
                                        <Input id="coverMainColorHex" type="text" invalid={false} placeholder="#FFFFFF"
                                               value={edited.coverMainColorHex ?? '#FFFFFF'}
                                               onChange={(e) => setEdited({...edited, coverMainColorHex: e.target.value})}
                                               className="h-10 flex-1"/>
                                    </div>
                                </Field>
                                {isAdmin && (
                                    <Field label={t('invitationLandingTemplate', {ns: 'dashboard'})}>
                                        <Select
                                            id="invitationLandingTemplate"
                                            block
                                            value={edited.invitationLandingTemplate ?? ''}
                                            onChange={(e) => setEdited({...edited, invitationLandingTemplate: e.target.value || undefined})}
                                            placeholder={t('selectInvitationLandingTemplate', {ns: 'dashboard'})}
                                            options={[
                                                {value: 'safari', label: t('templateSafari', {ns: 'dashboard'})},
                                                {value: 'snow', label: t('templateSnow', {ns: 'dashboard'})},
                                                {value: 'jungle', label: t('templateJungle', {ns: 'dashboard'})},
                                                {value: 'beach', label: t('templateBeach', {ns: 'dashboard'})},
                                                {value: 'italy', label: t('templateItaly', {ns: 'dashboard'})},
                                                {value: 'japon', label: t('templateJapon', {ns: 'dashboard'})},
                                                {value: 'rocky', label: t('templateRocky', {ns: 'dashboard'})},
                                                {value: 'cruise', label: t('templateCruise', {ns: 'dashboard'})},
                                                {value: 'newyork', label: t('templateNewYork', {ns: 'dashboard'})},
                                                {value: 'international', label: t('templateInternational', {ns: 'dashboard'})},
                                                {value: 'golf', label: t('templateGolf', {ns: 'dashboard'})}
                                            ]}
                                        />
                                    </Field>
                                )}
                                <div className="flex flex-col gap-2.5 pt-1">
                                    {isAdmin && (
                                        <SwitchField
                                            label={t('invertedLogo', {ns: 'dashboard'})}
                                            checked={edited.invertedLogo ?? false}
                                            onChange={(v) => setEdited({...edited, invertedLogo: v})}
                                        />
                                    )}
                                    {isAdmin && (
                                        <SwitchField
                                            label={t('webLinkQRCredits', {ns: 'dashboard'})}
                                            checked={edited.webLinkQRCredits ?? false}
                                            onChange={(v) => setEdited({...edited, webLinkQRCredits: v})}
                                        />
                                    )}
                                    <SwitchField
                                        label={t('showLogoInIntro', {ns: 'dashboard'})}
                                        checked={edited.showLogoInIntro ?? false}
                                        onChange={(v) => setEdited({...edited, showLogoInIntro: v})}
                                    />
                                </div>
                            </div>
                            <div className="flex-1 min-w-[280px] flex flex-col gap-3.5">
                                {isAdmin && (
                                    <Field label={t('dedicationText', {ns: 'dashboard'})}>
                                        <textarea
                                            value={edited.dedicationText ?? ''}
                                            onChange={(e) => setEdited({...edited, dedicationText: e.target.value || undefined})}
                                            rows={3}
                                            className="w-full rounded-xl border border-neutralBlack-200 px-3.5 py-2.5 text-sm resize-y focus:outline-none focus:border-neutralBlack-900 shadow-sm"
                                        />
                                    </Field>
                                )}
                                <Field label={t('introductionText', {ns: 'dashboard'})}>
                                    <textarea
                                        value={edited.introductionText ?? ''}
                                        onChange={(e) => setEdited({...edited, introductionText: e.target.value || undefined})}
                                        rows={3}
                                        placeholder={t('agencyDetail.noIntroTextPlaceholder', {ns: 'dashboard'})}
                                        className="w-full rounded-xl border border-neutralBlack-200 px-3.5 py-2.5 text-sm resize-y focus:outline-none focus:border-neutralBlack-900 shadow-sm"
                                    />
                                </Field>
                            </div>
                        </div>
                    </Section>

                    {/* Operación */}
                    <Section title={t('agencyDetail.operationSection', {ns: 'dashboard'})}>
                        <div className="flex gap-6 flex-wrap">
                            <div className="flex-1 min-w-[260px] flex flex-col gap-4">
                                <Field label={t('reviewer', {ns: 'dashboard'})}>
                                    <div className="flex gap-2">
                                        {(['Traveler', 'Agency'] as const).map((value) => {
                                            const selected = (edited.reviewer ?? 'Traveler') === value
                                            return (
                                                <button
                                                    type="button"
                                                    key={value}
                                                    onClick={() => setEdited({...edited, reviewer: value})}
                                                    className={`h-9 px-4 rounded-full text-sm font-medium border ${
                                                        selected
                                                            ? 'bg-neutralBlack-900 border-neutralBlack-900 text-white'
                                                            : 'bg-white border-neutralBlack-200 text-neutralBlack-800 hover:bg-neutralBlack-50'
                                                    }`}
                                                >
                                                    {value === 'Traveler' ? t('reviewerTraveler', {ns: 'dashboard'}) : t('reviewerAgency', {ns: 'dashboard'})}
                                                </button>
                                            )
                                        })}
                                    </div>
                                </Field>
                                <Field label={t('agencyDetail.accountManagerAssigned', {ns: 'dashboard'})}>
                                    <span className="inline-flex items-center gap-1.5 w-fit h-9 px-4 rounded-full bg-neutralBlack-900 text-white text-sm font-medium">
                                        {agency.accountManagerName ?? t('packs.statusNotAssigned', {ns: 'dashboard'})}
                                    </span>
                                    <span className="text-xs text-neutralBlack-400 mt-1">{t('agencyDetail.accountManagerHelp', {ns: 'dashboard'})}</span>
                                </Field>
                            </div>
                            <div className="flex-1 min-w-[280px] flex flex-col gap-3.5">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                                    {isAdmin && (
                                        <Field label={t('numberOfPagesIncluded', {ns: 'dashboard'})}>
                                            <Input id="numberOfPagesIncluded" type="number" invalid={false}
                                                   value={edited.numberOfPagesIncluded ?? 60}
                                                   onChange={(e) => setEdited({...edited, numberOfPagesIncluded: parseInt(e.target.value) || 60})}
                                                   className="h-10"/>
                                        </Field>
                                    )}
                                    {isAdmin && (
                                        <Field label={t('tripbookAgencyBindingName', {ns: 'dashboard'})}>
                                            <Input id="tripbookAgencyBindingName" type="text" invalid={false}
                                                   value={edited.tripbookAgencyBindingName ?? ''}
                                                   onChange={(e) => setEdited({...edited, tripbookAgencyBindingName: e.target.value || undefined})}
                                                   className="h-10"/>
                                        </Field>
                                    )}
                                </div>
                                {isAdmin && (
                                    <Field label={t('clientPlannerUrl', {ns: 'dashboard'})}>
                                        <Input id="clientPlannerUrl" type="url" invalid={false}
                                               value={edited.clientPlannerUrl ?? ''}
                                               onChange={(e) => setEdited({...edited, clientPlannerUrl: e.target.value || undefined})}
                                               className="h-10"/>
                                    </Field>
                                )}
                                <div className="flex flex-col gap-2.5">
                                    <span className="text-xs text-neutralBlack-500">{t('invitationEngagementFlow', {ns: 'dashboard'})}</span>
                                    <SwitchField
                                        label={t('captureItinerary', {ns: 'dashboard'})}
                                        checked={edited._captureItinerary ?? true}
                                        onChange={(v) => setEdited({...edited, _captureItinerary: v})}
                                    />
                                    <SwitchField
                                        label={t('captureDIY', {ns: 'dashboard'})}
                                        checked={edited._captureDIY ?? true}
                                        onChange={(v) => setEdited({...edited, _captureDIY: v})}
                                    />
                                </div>
                            </div>
                        </div>
                    </Section>

                    {/* Referral */}
                    {isAdmin && (
                        <Section title={t('referral', {ns: 'dashboard'})}>
                            {edited.referredByAgencyId ? (
                                <div className="flex flex-col gap-3.5">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[14px] font-medium">{referralAgencyName}</span>
                                        <button
                                            type="button"
                                            className="text-[12px] text-neutralBlack-500 underline"
                                            onClick={() => setEdited({
                                                ...edited,
                                                referredByAgencyId: undefined,
                                                referralCommissionPct: undefined,
                                                referralValidFrom: undefined,
                                                referralValidUntil: undefined,
                                            })}
                                        >
                                            {t('remove', {ns: 'dashboard'})}
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
                                        <Field label={t('referralCommissionPct', {ns: 'dashboard'})}>
                                            <Input
                                                id="referralCommissionPct"
                                                type="number" min="0" max="100" step="0.1" invalid={false}
                                                value={edited.referralCommissionPct != null ? edited.referralCommissionPct * 100 : 10}
                                                onChange={(e) => setEdited({...edited, referralCommissionPct: (parseFloat(e.target.value) || 0) / 100})}
                                                className="h-10"/>
                                        </Field>
                                        <Field label={t('referralValidFrom', {ns: 'dashboard'})}>
                                            <input
                                                type="date"
                                                value={edited.referralValidFrom ? new Date(edited.referralValidFrom).toISOString().split('T')[0] : ''}
                                                onChange={(e) => setEdited({...edited, referralValidFrom: e.target.value ? new Date(e.target.value).getTime() : undefined})}
                                                className="h-10 border border-neutralBlack-200 rounded-lg px-3 text-sm bg-white focus:outline-none focus:border-neutralBlack-900"/>
                                        </Field>
                                        <Field label={t('referralValidUntil', {ns: 'dashboard'})}>
                                            <input
                                                type="date"
                                                value={edited.referralValidUntil ? new Date(edited.referralValidUntil).toISOString().split('T')[0] : ''}
                                                onChange={(e) => setEdited({...edited, referralValidUntil: e.target.value ? new Date(e.target.value).getTime() : undefined})}
                                                className="h-10 border border-neutralBlack-200 rounded-lg px-3 text-sm bg-white focus:outline-none focus:border-neutralBlack-900"/>
                                        </Field>
                                    </div>
                                </div>
                            ) : (
                                <Field label={t('referralAgency', {ns: 'dashboard'})}>
                                    <AutocompleteSearch
                                        options={referralAgencyOptions}
                                        placeholder={t('searchYourAgency', {ns: 'dashboard'})}
                                        searchQuery={referralSearch}
                                        onSearchQueryChange={setReferralSearch}
                                        onSelectOption={(option) => {
                                            setEdited({
                                                ...edited,
                                                referredByAgencyId: option.value,
                                                referralCommissionPct: 0.10,
                                                referralValidFrom: Date.now(),
                                            })
                                            setReferralSearch('')
                                        }}
                                    />
                                </Field>
                            )}
                        </Section>
                    )}

                    {/* Referrals realizados por esta agencia */}
                    {isAdmin && (
                        <Section title={t('referralsMade', {ns: 'dashboard'})}>
                            {referralsLoading ? (
                                <div className="text-center py-6 text-sm text-neutralBlack-500">
                                    {t('loading', {ns: 'dashboard'})}
                                </div>
                            ) : referrals.length === 0 ? (
                                <div className="text-center py-6 text-sm text-neutralBlack-500">
                                    {t('noReferralsMadeYet', {ns: 'dashboard'})}
                                </div>
                            ) : (
                                <div className="flex flex-col">
                                    {referrals.map((r) => (
                                        <div key={r.id} className="flex items-center gap-4 py-3 border-b border-neutralBlack-100 last:border-0">
                                            <span className="flex-1 min-w-0 text-sm font-medium text-neutralBlack-900 truncate">
                                                {agencyNameById.get(r.referredAgencyId) ?? r.referredAgencyId}
                                            </span>
                                            <span className="text-xs text-neutralBlack-500">
                                                {(r.commissionPct * 100).toFixed(1)}%
                                            </span>
                                            <span className="text-xs text-neutralBlack-500">
                                                {new Date(r.validFrom).toLocaleDateString()}
                                                {r.validUntil ? ` - ${new Date(r.validUntil).toLocaleDateString()}` : ''}
                                            </span>
                                            <span className={`text-xs font-medium rounded-full px-2.5 py-1 ${
                                                r.status === 'ACTIVE'
                                                    ? 'bg-success/10 text-success'
                                                    : 'bg-neutralBlack-100 text-neutralBlack-600'
                                            }`}>
                                                {t(`referralStatus.${r.status}`, {ns: 'dashboard'})}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Section>
                    )}

                    {/* Códigos de invitación */}
                    <Section title={t('invitationCodes', {ns: 'dashboard'})}>
                        {!agency.invitationCodes?.length ? (
                            <div className="text-center py-6 text-sm text-neutralBlack-500">
                                {t('noCodesYet', {ns: 'dashboard'})}
                            </div>
                        ) : (
                            <div className="flex flex-col">
                                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                                {agency.invitationCodes.map((c: any) => (
                                    <div key={c.id} className="flex items-center gap-4 py-3 border-b border-neutralBlack-100 last:border-0">
                                        <span className="flex-1 min-w-0 text-sm font-medium tracking-wide text-neutralBlack-900 font-mono select-all">
                                            {c.code}
                                        </span>
                                        {c.stripeProductCode
                                            ? <span className="text-xs font-medium bg-orange/10 text-orange rounded-full px-2.5 py-1">{t('paid', {ns: 'dashboard'})}</span>
                                            : <span className="text-xs font-medium bg-success/10 text-success rounded-full px-2.5 py-1">{t('free', {ns: 'dashboard'})}</span>
                                        }
                                    </div>
                                ))}
                            </div>
                        )}
                    </Section>

                    {/* Paquetes de TripBooks */}
                    <Section
                        title={t('tripbookPackages', {ns: 'dashboard'})}
                        headerAccessory={packages.length > 0 && (
                            <span className="text-xs bg-neutralBlack-100 text-neutralBlack-600 rounded-full px-2.5 py-1 font-medium">
                                {t('totalPackagesAmount', {ns: 'dashboard'})}: {formatCurrency(totalPackages, currency)}
                            </span>
                        )}
                        actions={!showAddPackage && (
                            <Button variant="primary" size="small" className="max-h-[36px]" onClick={() => setShowAddPackage(true)}>
                                + {t('addPackage', {ns: 'dashboard'})}
                            </Button>
                        )}
                    >
                        {showAddPackage && (
                            <div className="border border-neutralBlack-200 rounded-xl p-4 flex flex-col gap-3 bg-neutralBlack-50">
                                {isAdmin && (
                                    <div role="radiogroup" aria-label={t('paymentPlans.modeLabel', {ns: 'dashboard'})} className="flex gap-1 p-1 bg-neutralBlack-100 rounded-full w-fit">
                                        {(['manual', 'stripe'] as const).map((mode) => (
                                            <button
                                                key={mode}
                                                type="button"
                                                role="radio"
                                                aria-checked={addPackageMode === mode}
                                                onClick={() => setAddPackageMode(mode)}
                                                className={`px-3.5 py-1.5 rounded-full text-[13px] transition-colors ${
                                                    addPackageMode === mode
                                                        ? 'bg-white text-neutralBlack-900 font-medium shadow-sm'
                                                        : 'text-neutralBlack-500 hover:text-neutralBlack-900'
                                                }`}
                                            >
                                                {t(mode === 'manual' ? 'paymentPlans.modeManual' : 'paymentPlans.modeStripe', {ns: 'dashboard'})}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {isAdmin && addPackageMode === 'stripe' ? (
                                    // Fuera de la zona euro no se repercute IVA; el comercial puede cambiarlo.
                                    <NewPaymentPlanForm
                                        currency={currency}
                                        defaultVatRate={currency === 'EUR' ? 0.21 : 0}
                                        agencyPages={agencyPages}
                                        formatMoney={(amount) => formatCurrency(amount, currency)}
                                        onSubmit={(payload) => paymentPlans.create(payload).then(() => setShowAddPackage(false))}
                                        onCancel={() => setShowAddPackage(false)}
                                    />
                                ) : (
                                <>
                                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                                    <Field label={t('packageDate', {ns: 'dashboard'})}>
                                        <input
                                            type="date"
                                            value={pkgDate}
                                            onChange={(e) => setPkgDate(e.target.value)}
                                            className="h-10 border border-neutralBlack-200 rounded-lg px-3 text-sm bg-white focus:outline-none focus:border-neutralBlack-900"
                                        />
                                    </Field>
                                    <Field label={t('packageQuantity', {ns: 'dashboard'})}>
                                        <Input id="pkgQuantity" type="number" min="1" invalid={false} placeholder="50"
                                               value={pkgQuantity} onChange={(e) => setPkgQuantity(e.target.value)} className="h-10"/>
                                    </Field>
                                    <Field label={t('packageUnitPrice', {ns: 'dashboard'})}>
                                        <Input id="pkgUnitPrice" type="number" min="0" step="0.01" invalid={false} placeholder="12.00"
                                               value={pkgUnitPrice} onChange={(e) => setPkgUnitPrice(e.target.value)} className="h-10"/>
                                    </Field>
                                    <Field label={t('packagePages', {ns: 'dashboard'})}>
                                        <Input id="pkgPages" type="number" min="1" invalid={false} placeholder={String(agencyPages)}
                                               value={pkgPages} onChange={(e) => setPkgPages(e.target.value)} className="h-10"/>
                                    </Field>
                                </div>
                                {pkgUnitPrice && pkgQuantity && (
                                    <div className="text-[13px] text-neutralBlack-500">
                                        {t('total', {ns: 'dashboard'})}: <span className="font-semibold text-neutralBlack-900">
                                            {formatCurrency(parseFloat(pkgUnitPrice) * parseInt(pkgQuantity, 10), currency)}
                                        </span>
                                    </div>
                                )}
                                <div className="flex gap-2">
                                    <Button variant="primary" size="small" className="max-h-[32px]"
                                            disabled={pkgSaving || !pkgUnitPrice || !pkgQuantity || !pkgDate}
                                            onClick={handleAddPackage}>
                                        {pkgSaving ? <IconSpinner className="size-4"/> : t('create', {ns: 'dashboard'})}
                                    </Button>
                                    <Button variant="secondary" size="small" className="max-h-[32px]" onClick={() => setShowAddPackage(false)}>
                                        {t('cancel', {ns: 'dashboard'})}
                                    </Button>
                                </div>
                                </>
                                )}
                            </div>
                        )}

                        {paymentPlans.error && (
                            <div role="alert" className="text-sm text-danger bg-danger/10 rounded-xl px-3.5 py-2.5">
                                {paymentPlans.error}
                            </div>
                        )}

                        {packagesLoading ? (
                            <div className="flex justify-center py-6">
                                <IconSpinner className="size-6"/>
                            </div>
                        ) : packages.length === 0 && pendingPlans.length === 0 ? (
                            <div className="text-center py-8 text-sm text-neutralBlack-500">
                                {t('noPackagesYet', {ns: 'dashboard'})}
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[600px] border-collapse text-[13.5px]">
                                    <thead>
                                        <tr>
                                            <th className="text-left py-2.5 font-medium text-[11px] uppercase tracking-wide text-neutralBlack-500 border-b border-neutralBlack-100">
                                                {t('packageDate', {ns: 'dashboard'})}
                                            </th>
                                            <th className="text-right py-2.5 px-3.5 font-medium text-[11px] uppercase tracking-wide text-neutralBlack-500 border-b border-neutralBlack-100">
                                                {t('packageQuantity', {ns: 'dashboard'})}
                                            </th>
                                            <th className="text-right py-2.5 px-3.5 font-medium text-[11px] uppercase tracking-wide text-neutralBlack-500 border-b border-neutralBlack-100">
                                                {t('packageUnitPrice', {ns: 'dashboard'})}
                                            </th>
                                            <th className="text-right py-2.5 px-3.5 font-medium text-[11px] uppercase tracking-wide text-neutralBlack-500 border-b border-neutralBlack-100">
                                                {t('packagePages', {ns: 'dashboard'})}
                                            </th>
                                            <th className="text-right py-2.5 font-medium text-[11px] uppercase tracking-wide text-neutralBlack-500 border-b border-neutralBlack-100">
                                                {t('total', {ns: 'dashboard'})}
                                            </th>
                                            <th className="w-[80px] border-b border-neutralBlack-100"/>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {/* Cobros con Stripe sin ningún pago: aún no son un pack ni suman en los totales */}
                                        {pendingPlans.map((plan) => (
                                            <React.Fragment key={`plan-${plan.id}`}>
                                                <tr className="border-b border-neutralBlack-100 text-neutralBlack-500">
                                                    <td className="py-3 whitespace-nowrap">
                                                        <div className="flex flex-col items-start gap-1">
                                                            {formatDate(plan.date)}
                                                            <PaymentPlanToggle plan={plan} expanded={expandedPlanId === plan.id} onToggle={() => togglePlan(plan.id)}/>
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-3.5 text-right font-medium">{plan.quantity}</td>
                                                    <td className="py-3 px-3.5 text-right">{formatCurrency(plan.unitPrice, currency)}</td>
                                                    <td className="py-3 px-3.5 text-right">{plan.numberOfPagesIncluded ?? agencyPages}</td>
                                                    <td className="py-3 text-right font-semibold">{formatCurrency(plan.netAmount, currency)}</td>
                                                    <td/>
                                                </tr>
                                                {expandedPlanId === plan.id && planDetailsRow(plan)}
                                            </React.Fragment>
                                        ))}
                                        {packages.map((pkg) => editingPkgId === pkg.id ? (
                                            <tr key={pkg.id} className="border-b border-neutralBlack-100">
                                                <td className="py-2 pr-2">
                                                    <input
                                                        type="date"
                                                        value={editPkgDate}
                                                        onChange={(e) => setEditPkgDate(e.target.value)}
                                                        className="h-9 border border-neutralBlack-200 rounded-lg px-2 text-[13px] bg-white focus:outline-none focus:border-neutralBlack-900 w-full"
                                                    />
                                                </td>
                                                <td className="py-2 px-2">
                                                    <Input id={`edit-qty-${pkg.id}`} type="number" min="1" invalid={false}
                                                           value={editPkgQuantity} onChange={(e) => setEditPkgQuantity(e.target.value)}
                                                           className="h-9 text-right"/>
                                                </td>
                                                <td className="py-2 px-2">
                                                    <Input id={`edit-price-${pkg.id}`} type="number" min="0" step="0.01" invalid={false}
                                                           value={editPkgUnitPrice} onChange={(e) => setEditPkgUnitPrice(e.target.value)}
                                                           className="h-9 text-right"/>
                                                </td>
                                                <td className="py-2 px-2">
                                                    <Input id={`edit-pages-${pkg.id}`} type="number" min="1" invalid={false} placeholder={String(agencyPages)}
                                                           value={editPkgPages} onChange={(e) => setEditPkgPages(e.target.value)}
                                                           className="h-9 text-right"/>
                                                </td>
                                                <td className="py-2 text-right font-semibold text-[13px]">
                                                    {editPkgUnitPrice && editPkgQuantity
                                                        ? formatCurrency(parseFloat(editPkgUnitPrice) * parseInt(editPkgQuantity, 10), currency)
                                                        : '—'}
                                                </td>
                                                <td className="py-2 pl-2">
                                                    <div className="flex gap-1 justify-end">
                                                        <Button variant="primary" size="small" className="max-h-[28px] px-2"
                                                                disabled={editPkgSaving} onClick={handleUpdatePackage}>
                                                            {editPkgSaving ? <IconSpinner className="size-3"/> : '✓'}
                                                        </Button>
                                                        <Button variant="secondary" size="small" className="max-h-[28px] px-2"
                                                                onClick={() => setEditingPkgId(null)}>
                                                            ✕
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            <React.Fragment key={pkg.id}>
                                            <tr className="border-b border-neutralBlack-100 last:border-0 hover:bg-neutralBlack-50 transition-colors group">
                                                <td className="py-3 text-neutralBlack-700 whitespace-nowrap">
                                                    {planByPackageId.has(pkg.id) ? (
                                                        <div className="flex flex-col items-start gap-1">
                                                            {formatDate(pkg.date)}
                                                            <PaymentPlanToggle plan={planByPackageId.get(pkg.id)!}
                                                                               expanded={expandedPlanId === planByPackageId.get(pkg.id)!.id}
                                                                               onToggle={() => togglePlan(planByPackageId.get(pkg.id)!.id)}/>
                                                        </div>
                                                    ) : formatDate(pkg.date)}
                                                </td>
                                                <td className="py-3 px-3.5 text-right font-medium text-neutralBlack-900">
                                                    {pkg.quantity}
                                                </td>
                                                <td className="py-3 px-3.5 text-right text-neutralBlack-500">
                                                    {formatCurrency(pkg.unitPrice, currency)}
                                                </td>
                                                <td className="py-3 px-3.5 text-right text-neutralBlack-700">
                                                    {pkg.numberOfPagesIncluded ?? agencyPages}
                                                </td>
                                                <td className="py-3 text-right font-semibold text-neutralBlack-900">
                                                    {formatCurrency(pkg.totalAmount, currency)}
                                                </td>
                                                <td className="py-3 pl-2">
                                                    <div className="flex gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <button
                                                            onClick={() => startEditPackage(pkg)}
                                                            className="text-xs text-neutralBlack-500 hover:text-neutralBlack-900 px-1.5 py-0.5 rounded hover:bg-neutralBlack-200 transition-colors"
                                                            title={t('edit', {ns: 'dashboard'})}
                                                        >
                                                            ✏️
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeletePackage(pkg.id)}
                                                            disabled={deletingPkgId === pkg.id}
                                                            className="text-xs text-neutralBlack-500 hover:text-danger px-1.5 py-0.5 rounded hover:bg-danger/10 transition-colors disabled:opacity-40"
                                                            title={t('delete', {ns: 'dashboard'})}
                                                        >
                                                            {deletingPkgId === pkg.id ? <IconSpinner className="size-3"/> : '🗑️'}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                            {planByPackageId.has(pkg.id) && expandedPlanId === planByPackageId.get(pkg.id)!.id
                                                && planDetailsRow(planByPackageId.get(pkg.id)!)}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                    {packages.length > 1 && (
                                        <tfoot>
                                            <tr className="border-t-2 border-neutralBlack-200">
                                                <td colSpan={3} className="py-3 text-[13px] font-semibold text-neutralBlack-500 uppercase tracking-wide">
                                                    {t('totalPackagesAmount', {ns: 'dashboard'})}
                                                </td>
                                                <td className="py-3 text-right font-bold text-[15px] text-neutralBlack-900">
                                                    {formatCurrency(totalPackages, currency)}
                                                </td>
                                                <td/>
                                            </tr>
                                        </tfoot>
                                    )}
                                </table>
                            </div>
                        )}
                    </Section>

                    {/* Usuarios de la agencia — solo gestionable por 51trips */}
                    {isAdmin && (
                        <AgencyUsersSection agencyId={agencyId}/>
                    )}

                </div>
                )}
            </div>

            {isSaving && (
                <div className="absolute inset-0 bg-neutralBlack-100 opacity-75 flex rounded-xl">
                    <IconSpinner className="size-16 m-auto"/>
                </div>
            )}

            <ConfirmDialog
                ref={confirmDeleteDialog}
                onConfirm={handleDelete}
                title={t('deleteAgency', {ns: 'dashboard'})}
                confirmLabel={t('delete', {ns: 'dashboard'})}
                cancelLabel={t('cancel', {ns: 'dashboard'})}
            />
        </div>
    )
}

const MiniStat: React.FC<{label: string; value: string; note?: string}> = ({label, value, note}) => (
    <div className="flex-1 min-w-[150px] bg-white border border-neutralBlack-200 rounded-2xl shadow-sm px-[18px] py-4 flex flex-col gap-1.5">
        <span className="text-[11.5px] font-medium tracking-wide uppercase text-neutralBlack-500">{label}</span>
        <span className="text-[22px] font-semibold tracking-tight text-neutralBlack-900">{value}</span>
        {note && <span className="text-xs text-neutralBlack-500">{note}</span>}
    </div>
)

const TabButton: React.FC<{label: string; active?: boolean; disabled?: boolean; onClick?: () => void}> = ({label, active, disabled, onClick}) => {
    const {t} = useTranslation()
    return (
    <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        title={disabled ? t('metrics.comingSoon', {ns: 'dashboard'}) : undefined}
        className={`pb-3 -mb-px border-b-2 text-sm ${
            disabled
                ? 'border-transparent text-neutralBlack-300 cursor-not-allowed'
                : active
                    ? 'border-neutralBlack-900 text-neutralBlack-900 font-medium'
                    : 'border-transparent text-neutralBlack-500 cursor-pointer hover:text-neutralBlack-900'
        }`}
    >
        {label}
    </button>
    )
}

const Section: React.FC<{title: string; headerAccessory?: React.ReactNode; actions?: React.ReactNode; children: React.ReactNode}> = ({title, headerAccessory, actions, children}) => (
    <section className="bg-white border border-neutralBlack-200 rounded-2xl shadow-sm p-5 flex flex-col gap-4">
        <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[11.5px] font-medium tracking-wide uppercase text-neutralBlack-500">{title}</span>
            {headerAccessory}
            <span className="flex-1 min-w-[8px]"/>
            {actions}
        </div>
        {children}
    </section>
)

const Field: React.FC<{label: string; required?: boolean; children: React.ReactNode}> = ({label, required, children}) => (
    <label className="flex flex-col gap-1.5">
        <span className="text-[12.5px] text-neutralBlack-600">
            {required && <span className="text-danger">* </span>}
            {label}
        </span>
        {children}
    </label>
)

const SwitchField: React.FC<{label: string; checked: boolean; onChange: (value: boolean) => void}> = ({label, checked, onChange}) => (
    <button
        type="button"
        onClick={() => onChange(!checked)}
        className="flex items-center gap-3 w-fit"
    >
        <span className={`w-[42px] h-[24px] rounded-full flex-none flex items-center px-0.5 transition-colors ${checked ? 'bg-neutralBlack-900' : 'bg-neutralBlack-200'}`}>
            <span className={`w-[18px] h-[18px] rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-[18px]' : 'translate-x-0'}`}/>
        </span>
        <span className="text-sm text-neutralBlack-900">{label}</span>
    </button>
)


function marketToCurrency(market?: string): string {
    if (market === 'usa' || market === 'US') return 'USD'
    if (market === 'canada' || market === 'CA') return 'CAD'
    if (market === 'australia' || market === 'AU') return 'AUD'
    return 'EUR'
}

function formatCurrency(amount: number, currency: string): string {
    const locale = currency === 'USD' ? 'en-US' : currency === 'CAD' ? 'en-CA' : currency === 'AUD' ? 'en-AU' : 'es-ES'
    // useGrouping must be forced 'always': es-ES's 'auto' default uses "min2"
    // grouping, which drops the thousands separator for 4-digit values (e.g. 7829).
    return new Intl.NumberFormat(locale, {style: 'currency', currency, useGrouping: 'always'} as any).format(amount)
}

function formatDate(timestamp: number): string {
    return new Intl.DateTimeFormat('es-ES', {day: '2-digit', month: '2-digit', year: 'numeric'}).format(new Date(timestamp))
}

function extractTemplateFromUrl(url?: string): string {
    if (!url) return ''
    const filename = url.split('/').pop() ?? ''
    return filename.split('.')[0].toLowerCase()
}
